import {
  Alert,
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack
} from '@mui/material'
import { ActionRequest, Project } from '../types'
import { useTranslation } from 'react-i18next'
import useDialog from '../hooks/useDialog'
import { DatePicker } from '@mui/x-date-pickers'
import { MouseEvent } from 'react'
import UpdateActionRequestStateInput from '../types/UpdateActionRequestState'
import { RequestStatus } from '../enums/RequestStatus'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import { LoadingButton } from '@mui/lab'
import { updateRequestState } from '../services/requestService'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../redux'
import { setProject } from '../redux/slices/projects'

export interface ProjectExtendButtonProps extends Omit<ButtonProps, 'children'> {
  project: Project
  actionRequest: ActionRequest
  beforeSubmit?: (
    values: UpdateActionRequestStateInput,
    helpers: FormikHelpers<UpdateActionRequestStateInput>
  ) => void
  afterSubmit?: (actionRequest: ActionRequest) => void
  label?: string
}

export default function ProjectExtendButton({
  project,
  actionRequest,
  beforeSubmit,
  afterSubmit,
  label,
  onClick,
  ...props
}: ProjectExtendButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The redux project state.
   */
  const reduxProject = useSelector((state: RootState) => state.projects.project)

  /**
   * The initial values.
   */
  const initialValues: UpdateActionRequestStateInput = {
    status: RequestStatus.REJECTED,
    newEndDate: new Date(project.endDate)
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    newEndDate: Yup.date().required(
      t('isRequired', {
        field: t('endDate').toLowerCase()
      })
    )
  })

  /**
   * Handle the "click" event.
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    openDialog()
    onClick?.(event)
  }

  /**
   * Handle the submit.
   */
  const handleSubmit = async (
    values: UpdateActionRequestStateInput,
    { setSubmitting, ...helpers }: FormikHelpers<UpdateActionRequestStateInput>
  ) => {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })
      const updatedActionRequest = await updateRequestState(actionRequest.id, values)

      if (reduxProject && reduxProject.id === project.id && values?.newEndDate) {
        dispatch(
          setProject({
            ...reduxProject,
            endDate: values.newEndDate.toISOString()
          })
        )
      }

      dispatch(createSuccessNotification(t('projectStateUpdateSuccess')))

      closeDialog()
      afterSubmit?.(updatedActionRequest)
    } catch (_error) {
      setSubmitting(false)

      dispatch(createErrorNotification(t('projectStateUpdateError')))
    }
  }

  return (
    <>
      <Button {...props} onClick={handleClick}>
        {label ?? t('extendProject')}
      </Button>

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{t('extendProject')}</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, touched, errors, isSubmitting, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Alert severity="warning" sx={{ mb: 4 }}>
                  {t('extendProjectWarning', {
                    project: project.name
                  })}
                </Alert>

                <DatePicker
                  name="newEndDate"
                  label={t('newEndDate')}
                  value={values.newEndDate}
                  sx={{ mt: 2, width: '100%' }}
                  onChange={(value) => {
                    if (value !== null) {
                      setFieldValue('newEndDate', value)
                    }
                  }}
                />
                {Boolean(touched.newEndDate) && errors.newEndDate && (
                  <FormHelperText error>{errors.newEndDate}</FormHelperText>
                )}

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={2}
                  sx={{ mt: 6 }}
                >
                  <Button variant="text" color="inherit" onClick={closeDialog}>
                    {t('cancel')}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={isSubmitting}
                  >
                    {t('save')}
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}
